import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api-client";
import { customerKeys } from "@/features/customer/api";
import type {
  Tag,
  TagCreateRequest,
  TagUpdateRequest,
  TagWire,
} from "@/features/tag/types";

function toTag(wire: TagWire): Tag {
  return {
    id: wire.id!,
    name: wire.name!,
    color: wire.color!,
    sortOrder: wire.sortOrder ?? 0,
  };
}

export const tagKeys = {
  all: ["tags"] as const,
  list: () => [...tagKeys.all, "list"] as const,
};

export function useTags() {
  return useQuery({
    queryKey: tagKeys.list(),
    queryFn: async () => (await apiRequest<TagWire[]>("/tags")).map(toTag),
  });
}

export function useCreateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TagCreateRequest) =>
      apiRequest<TagWire>("/tags", { method: "POST", body }).then(toTag),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: tagKeys.all }),
  });
}

export function useUpdateTag(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TagUpdateRequest) =>
      apiRequest<TagWire>(`/tags/${id}`, { method: "PATCH", body }).then(toTag),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tagKeys.all });
      // A rename/recolor changes how badges render on customers.
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useDeleteTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiRequest<void>(`/tags/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tagKeys.all });
      // A deleted tag drops off every customer + the chip counts.
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}
